import React from 'react';
import Matter from '../../../common/model/matter/Matter';
import Moon from '../../../common/model/global/Moon';
import { UserRole } from '../../../common/model/user/UserRole';
import HwzComponent from '../../../common/component/HwzComponent';
import Director from './Director';
import './MatterPanel.less';
import StringUtil from '../../../common/util/StringUtil';
import DateUtil from '../../../common/util/DateUtil';
import AnimateUtil from '../../../common/util/AnimateUtil';
import MessageBoxUtil from '../../../common/util/MessageBoxUtil';
import PreviewerHelper from '../../../pages/widget/previewer/PreviewerHelper';
import Expanding from '../../widget/Expanding';
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EllipsisOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  FileImageOutlined,
} from '@ant-design/icons';
import { Checkbox, Dropdown, Menu, Tooltip, Modal, Select, Input } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import SafeUtil from '../../../common/util/SafeUtil';
import Lang from '../../../common/model/global/Lang';
import MatterDeleteModal from './MatterDeleteModal';
import ApprovalModal from './ApprovalModal';
import { SpaceMemberRole } from '../../../common/model/space/member/SpaceMemberRole';
import User from '../../../common/model/user/User';

const { Option } = Select;

interface IProps {
  matter: Matter;
  mode: 'normal' | 'space'; // normal:正常模式，space:空间模式
  spaceMemberRole?: SpaceMemberRole; // 用户在当前空间下的角色,只有在space模式下才有值
  director?: Director;
  onCreateDirectoryCallback?: () => any;
  onDeleteSuccess?: () => any;
  onCheckMatter?: (matter?: Matter) => any;
  onPreviewImage?: (matter: Matter) => any;
  onGoToDirectory?: (id: string) => any;
  onGoDetail?: (matter: Matter) => any;
  isMobile?: boolean; // 新增：移动端标识
  onApproveMatter?: (matter: Matter) => any; // 新增：审批通过回调
  onRejectMatter?: (matter: Matter) => any; // 新增：审批拒绝回调
}

interface IState {
  approvalModalVisible: boolean;
  currentMatter: Matter | null;
  pdfConvertModalVisible: boolean;
  pdfConvertFormat: string;
  pdfConverting: boolean;
}

export default class MatterPanel extends HwzComponent<IProps, IState> {
  user: User = Moon.getSingleton().user;
  renameMatterName: string = '';
  renamingLoading: boolean = false;
  showMore: boolean = false;

  inputRef = React.createRef<HTMLInputElement>();

  constructor(props: IProps) {
    super(props);
    this.state = {
      approvalModalVisible: false,
      currentMatter: null,
      pdfConvertModalVisible: false,
      pdfConvertFormat: 'PNG',
      pdfConverting: false,
    };
  }

  // 获取审批状态对应的显示文本
  getApprovalText(approval: number, outsendStatus: number): string {
    if (approval === 0 && outsendStatus === 0) {
      return Lang.t('matter.noneedApproval');
    }
    
    if (approval === 0 && outsendStatus !== 0) {
      switch (outsendStatus) {
        case 1:
          return Lang.t('matter.waitApproval');
        case 2:
          return Lang.t('matter.approvalPass');
        case 3:
          return Lang.t('matter.approvalReject');
        default:
          return Lang.t('matter.unknown');
      }
    }
    
    if (outsendStatus === 0 && approval !== 0) {
      switch (approval) {
        case 1:
          return Lang.t('matter.waitApproval');
        case 2:
          return Lang.t('matter.approvalPass');
        case 3:
          return Lang.t('matter.approvalReject');
        default:
          return Lang.t('matter.unknown');
      }
    }
    
    if (approval === 1 || outsendStatus === 1) {
      return Lang.t('matter.waitApproval');
    }
    
    if (approval === 3 || outsendStatus === 3) {
      return Lang.t('matter.approvalReject');
    }
    
    if (approval === 2 && outsendStatus === 2) {
      return Lang.t('matter.approvalPass');
    }
    
    return Lang.t('matter.unknown');
  }

  // 获取审批状态对应的图标
  getApprovalIcon(approval: number, outsendStatus: number): React.ReactNode {
    if (approval === 0 && outsendStatus === 0) {
      return null;
    }
    
    if (approval === 0 && outsendStatus !== 0) {
      switch (outsendStatus) {
        case 1:
          return <ClockCircleOutlined className="approval-icon pending" />;
        case 2:
          return <CheckCircleOutlined className="approval-icon approved" />;
        case 3:
          return <CloseCircleOutlined className="approval-icon rejected" />;
        default:
          return null;
      }
    }
    
    if (outsendStatus === 0 && approval !== 0) {
      switch (approval) {
        case 1:
          return <ClockCircleOutlined className="approval-icon pending" />;
        case 2:
          return <CheckCircleOutlined className="approval-icon approved" />;
        case 3:
          return <CloseCircleOutlined className="approval-icon rejected" />;
        default:
          return null;
      }
    }
    
    if (approval === 1 || outsendStatus === 1) {
      return <ClockCircleOutlined className="approval-icon pending" />;
    }
    
    if (approval === 3 || outsendStatus === 3) {
      return <CloseCircleOutlined className="approval-icon rejected" />;
    }
    
    if (approval === 2 && outsendStatus === 2) {
      return <CheckCircleOutlined className="approval-icon approved" />;
    }
    
    return null;
  }

  // 获取审批状态对应的CSS类名
  getApprovalClassName(approval: number): string {
    switch (approval) {
      case 0:
        return 'approval-no';
      case 1:
        return 'approval-pending';
      case 2:
        return 'approval-approved';
      case 3:
        return 'approval-rejected';
      default:
        return 'approval-unknown';
    }
  }

  // 检查是否是PDF文件
  isPdfFile(matter: Matter): boolean {
    return matter.name?.toLowerCase().endsWith('.pdf') || false;
  }

  // 打开PDF转换模态框
  openPdfConvertModal = () => {
    this.setState({
      pdfConvertModalVisible: true,
      pdfConvertFormat: 'PNG',
    });
  };

  // 关闭PDF转换模态框
  closePdfConvertModal = () => {
    this.setState({
      pdfConvertModalVisible: false,
      pdfConverting: false,
    });
  };

  // PDF转换处理
  handlePdfConvert = async () => {
    const { matter } = this.props;
    const { pdfConvertFormat } = this.state;

    this.setState({ pdfConverting: true });

    try {
      matter.httpPdfToImage(
        pdfConvertFormat,
        (response: any) => {
          MessageBoxUtil.success(Lang.t('matter.pdfConvertSuccess'));
          this.closePdfConvertModal();
        },
        (error: string) => {
          MessageBoxUtil.error(error || Lang.t('matter.pdfConvertFailed'));
        },
        () => {
          this.setState({ pdfConverting: false });
        }
      );
    } catch (error) {
      MessageBoxUtil.error(Lang.t('matter.pdfConvertFailed'));
      console.error('PDF转换失败:', error);
      this.setState({ pdfConverting: false });
    }
  };

  // 如果在空间下，有些操作权限只对管理员和读写成员开放
  checkHandlePermission() {
    if (this.props.mode !== 'space') return true;
    
    if (this.props.spaceMemberRole === SpaceMemberRole.PREVIEW_ONLY || 
        this.props.spaceMemberRole === SpaceMemberRole.UPLOAD_PREVIEW) {
      return false;
    }
    
    return [SpaceMemberRole.READ_WRITE, SpaceMemberRole.ADMIN].includes(
      this.props.spaceMemberRole!
    );
  }

  checkAdminPermission() {
    return this.user.role === UserRole.ADMINISTRATOR; 
  }

  // 审批操作 - 紧急修复版，确保传递正确的审批状态值
  handleApprove = (matter: Matter) => {
    const fileName = matter.name || Lang.t('matter.unknownFile');
    Modal.confirm({
      title: Lang.t('matter.confirmApproval'),
      icon: <InfoCircleOutlined />,
      content: Lang.t('matter.confirmApproveFile').replace('{fileName}', fileName),
      onOk: () => {
        try {
          console.log('手机端审批通过，设置 approval=2');
          
          // 关键：必须在调用 httpApproval 之前设置正确的审批状态！
          matter.approval = 2;  // 审批通过 = 2
          matter.outsendStatus = 0; // 保持原状
          
          // 调用审批API
          matter.httpApproval(
            () => {
              console.log('审批API调用成功，approval应该为2');
              MessageBoxUtil.success(Lang.t('matter.approveSuccess').replace('{fileName}', fileName));
              
              // 成功后的处理
              if (this.props.onDeleteSuccess) {
                this.props.onDeleteSuccess(); // 触发父组件刷新
              }
              
              // 强制更新UI
              this.updateUI();
            },
            (error: string) => {
              MessageBoxUtil.error(error || Lang.t('operationFailed'));
              console.error('手机端审批失败:', error);
            }
          );
        } catch (error) {
          MessageBoxUtil.error(Lang.t('operationFailed'));
          console.error('审批操作异常:', error);
        }
      },
    });
  };

  handleReject = (matter: Matter) => {
    const fileName = matter.name || Lang.t('matter.unknownFile');
    Modal.confirm({
      title: Lang.t('matter.confirmReject'),
      icon: <InfoCircleOutlined />,
      content: Lang.t('matter.confirmRejectFile').replace('{fileName}', fileName),
      onOk: () => {
        try {
          console.log('手机端审批驳回，设置 approval=3');
          
          // 关键：必须在调用 httpApproval 之前设置正确的审批状态！
          matter.approval = 3;  // 审批驳回 = 3
          matter.outsendStatus = 0; // 保持原状
          
          // 调用审批API
          matter.httpApproval(
            () => {
              console.log('驳回API调用成功，approval应该为3');
              MessageBoxUtil.warning(Lang.t('matter.rejectSuccess').replace('{fileName}', fileName));
              
              // 成功后的处理
              if (this.props.onDeleteSuccess) {
                this.props.onDeleteSuccess(); // 触发父组件刷新
              }
              
              // 强制更新UI
              this.updateUI();
            },
            (error: string) => {
              MessageBoxUtil.error(error || Lang.t('operationFailed'));
              console.error('手机端驳回失败:', error);
            }
          );
        } catch (error) {
          MessageBoxUtil.error(Lang.t('operationFailed'));
          console.error('驳回操作异常:', error);
        }
      },
    });
  };

  // 移动端审批状态点击处理
  handleMobileApprovalClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const { matter } = this.props;
    
    // 只有待审批状态才可点击
    if (matter.approval !== 1 && matter.outsendStatus !== 1) {
      return;
    }
    
    this.showApprovalModal(matter);
  };

  showApprovalModal = (matter: Matter) => {
    this.setState({
      approvalModalVisible: true,
      currentMatter: matter
    });
  }

  handleApprovalModalClose = () => {
    this.setState({
      approvalModalVisible: false,
      currentMatter: null
    });
  };

  handleApprovalSuccess = () => {
    if (this.props.onDeleteSuccess) {
      this.props.onDeleteSuccess();
    }
    this.updateUI();
  };

  prepareRename() {
    const { matter, director } = this.props;
    if (director!.isEditing()) {
      console.error('导演正忙着，不予执行');
      return;
    }

    director!.renameMode = true;
    matter.editMode = true;
    this.renameMatterName = matter.name!;
    this.updateUI();
    setTimeout(() => {
      if (!this.inputRef.current) return;
      let dotIndex = matter.name!.lastIndexOf('.');
      if (dotIndex === -1) {
        AnimateUtil.setInputSelection(
          this.inputRef.current,
          0,
          this.renameMatterName.length
        );
      } else {
        AnimateUtil.setInputSelection(this.inputRef.current, 0, dotIndex);
      }
    });
  }

  deleteMatter() {
    MatterDeleteModal.open(
      this.props.mode === 'space',
      () => {
        this.props.matter.httpSoftDelete(() => {
          MessageBoxUtil.success(Lang.t('operationSuccess'));
          this.props.onDeleteSuccess!();
        });
      },
      () => {
        this.props.matter.httpDelete(() => {
          MessageBoxUtil.success(Lang.t('operationSuccess'));
          this.props.onDeleteSuccess!();
        });
      }
    );
  }

  changeMatterName(e: any) {
    this.renameMatterName = e.currentTarget.value;
    this.updateUI();
  }

  finishRename() {
    if (this.renamingLoading) {
      return;
    }
    const { matter, director } = this.props;
    this.renamingLoading = true;

    matter.httpRename(
      this.renameMatterName,
      () => {
        this.renamingLoading = false;
        MessageBoxUtil.success(Lang.t('operationSuccess'));
        director!.renameMode = false;
        matter.editMode = false;
      },
      (msg: string) => {
        this.renamingLoading = false;
        MessageBoxUtil.error(msg);
        director!.renameMode = false;
        matter.editMode = false;
      },
      () => this.updateUI()
    );
  }

  finishCreateDirectory() {
    const { matter, director, onCreateDirectoryCallback } = this.props;
    matter.name = this.renameMatterName;
    matter.httpCreateDirectory(
      () => {
        director!.createMode = false;
        matter.editMode = false;
        matter.assign(new Matter());
      },
      (msg: string) => {
        director!.createMode = false;
        matter.editMode = false;
        MessageBoxUtil.error(msg);
      },
      () => onCreateDirectoryCallback!()
    );
  }

  blurTrigger() {
    const { matter, director } = this.props;
    if (matter.editMode) {
      if (director!.createMode) {
        this.finishCreateDirectory();
      } else if (director!.renameMode) {
        this.finishRename();
      }
    }
  }

  enterTrigger(e: any) {
    if (e.key.toLowerCase() === 'enter') {
      this.inputRef.current!.blur();
    }
  }

  checkToggle(e: CheckboxChangeEvent) {
    this.props.matter.check = e.target.checked;
    this.props.onCheckMatter!(this.props.matter);
  }

  highLight() {
    this.inputRef.current!.select();
  }

  clickRow() {
    const { matter, director, onGoToDirectory, onPreviewImage } = this.props;
    if (director && director.isEditing()) {
      console.error('导演正忙着，不予执行');
      return;
    }
  
    if (matter.dir) {
      onGoToDirectory!(matter.uuid!);
    } else {
      if (matter.isImage()) {
        onPreviewImage!(matter);
      } else {
        // 传递符合接口的参数
        const hasDownloadPermission = this.checkDownloadPermission();
        const hasPrintPermission = false; // 打印权限被移除，设为false
        
        matter.preview(
          matter.getPreviewUrl(),  // 预览URL
          hasDownloadPermission,   // 下载权限
          hasPrintPermission       // 打印权限
        );
      }
    }
  }

  toggleHandles() {
    this.showMore = !this.showMore;
    this.updateUI();
  }

  // 检查是否有下载权限
  checkDownloadPermission(): boolean {
    if (this.props.mode !== 'space') return true;
    
    if (this.props.spaceMemberRole === SpaceMemberRole.PREVIEW_ONLY || 
        this.props.spaceMemberRole === SpaceMemberRole.UPLOAD_PREVIEW) {
      return false;
    }
    
    return [SpaceMemberRole.READ_ONLY, SpaceMemberRole.READ_WRITE, SpaceMemberRole.ADMIN].includes(
      this.props.spaceMemberRole!
    );
  }

  // 检查是否有删除权限
  checkDeletePermission(): boolean {
    if (this.props.mode !== 'space') return true;
    
    if (this.props.spaceMemberRole === SpaceMemberRole.PREVIEW_ONLY || 
        this.props.spaceMemberRole === SpaceMemberRole.UPLOAD_PREVIEW) {
      return false;
    }
    
    return [SpaceMemberRole.READ_WRITE, SpaceMemberRole.ADMIN].includes(
      this.props.spaceMemberRole!
    );
  }

  // 检查是否有重命名权限
  checkRenamePermission(): boolean {
    if (this.props.mode !== 'space') return true;
    
    if (this.props.spaceMemberRole === SpaceMemberRole.PREVIEW_ONLY || 
        this.props.spaceMemberRole === SpaceMemberRole.UPLOAD_PREVIEW) {
      return false;
    }
    
    return [SpaceMemberRole.READ_WRITE, SpaceMemberRole.ADMIN].includes(
      this.props.spaceMemberRole!
    );
  }

  // 检查是否有PDF转换权限
  checkPdfConvertPermission(): boolean {
    return this.checkDownloadPermission();
  }

  renderPcOperation() {
    const { matter, isMobile } = this.props;
    const approvalText = this.getApprovalText(matter.approval, matter.outsendStatus);
    const approvalIcon = this.getApprovalIcon(matter.approval, matter.outsendStatus);
    const approvalClassName = this.getApprovalClassName(matter.approval);
    const isPending = matter.approval === 1 || matter.outsendStatus === 1;
    
    // 移动端不显示行内审批状态，审批操作已移到下拉菜单
    if (isMobile) {
      return (
        <div className="right-part" style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
          <span className="matter-operation text-theme" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <Tooltip title={Lang.t('matter.fileDetail')}>
              <InfoCircleOutlined
                className="btn-action"
                onClick={(e) =>
                  SafeUtil.stopPropagationWrap(e)(this.props.onGoDetail?.(matter))
                }
              />
            </Tooltip>
            
            {this.checkRenamePermission() && (
              <Tooltip title={Lang.t('matter.rename')}>
                <EditOutlined
                  className="btn-action"
                  onClick={(e) =>
                    SafeUtil.stopPropagationWrap(e)(this.prepareRename())
                  }
                />
              </Tooltip>
            )}
            
            {this.checkDownloadPermission() && (
              <Tooltip title={Lang.t('matter.download')}>
                <DownloadOutlined
                  className="btn-action"
                  onClick={(e) =>
                    SafeUtil.stopPropagationWrap(e)(matter.download())
                  }
                />
              </Tooltip>
            )}
            
            {this.checkDeletePermission() && (
              <Tooltip title={Lang.t('matter.delete')}>
                <DeleteOutlined
                  className="btn-action text-danger"
                  onClick={(e) =>
                    SafeUtil.stopPropagationWrap(e)(this.deleteMatter())
                  }
                />
              </Tooltip>
            )}
          </span>
          
          <Tooltip title={Lang.t('matter.size')}>
            <span className="matter-size" style={{ flexShrink: 0, minWidth: '80px', margin: '0 10px' }}>
              {StringUtil.humanFileSize(matter.size)}
            </span>
          </Tooltip>
          
          <Tooltip title={Lang.t('matter.updateTime')}>
            <span className="matter-date mr10" style={{ flexShrink: 0, margin: '0 10px' }}>
              {DateUtil.simpleDateHourMinute(matter.updateTime)}
            </span>
          </Tooltip>
        </div>
      );
    }
    
    // PC端保持原有逻辑，显示行内审批状态
    return (
      <div className="right-part" style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
        <span className="matter-operation text-theme" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <Tooltip title={Lang.t('matter.fileDetail')}>
            <InfoCircleOutlined
              className="btn-action"
              onClick={(e) =>
                SafeUtil.stopPropagationWrap(e)(this.props.onGoDetail?.(matter))
              }
            />
          </Tooltip>
          
          {this.checkRenamePermission() && (
            <Tooltip title={Lang.t('matter.rename')}>
              <EditOutlined
                className="btn-action"
                onClick={(e) =>
                  SafeUtil.stopPropagationWrap(e)(this.prepareRename())
                }
              />
            </Tooltip>
          )}
          
          {this.checkDownloadPermission() && (
            <Tooltip title={Lang.t('matter.download')}>
              <DownloadOutlined
                className="btn-action"
                onClick={(e) =>
                  SafeUtil.stopPropagationWrap(e)(matter.download())
                }
              />
            </Tooltip>
          )}
          
          {this.checkDeletePermission() && (
            <Tooltip title={Lang.t('matter.delete')}>
              <DeleteOutlined
                className="btn-action text-danger"
                onClick={(e) =>
                  SafeUtil.stopPropagationWrap(e)(this.deleteMatter())
                }
              />
            </Tooltip>
          )}
        </span>
        
        <Tooltip title={Lang.t('matter.size')}>
          <span className="matter-size" style={{ flexShrink: 0, minWidth: '80px', margin: '0 10px' }}>
            {StringUtil.humanFileSize(matter.size)}
          </span>
        </Tooltip>
        
        <Tooltip title={Lang.t('matter.updateTime')}>
          <span className="matter-date mr10" style={{ flexShrink: 0, margin: '0 10px' }}>
            {DateUtil.simpleDateHourMinute(matter.updateTime)}
          </span>
        </Tooltip>

        {this.checkAdminPermission() && (
          <Tooltip title={approvalText}>
            <span 
              className={`matter-approval mr10 ${approvalClassName} ${isPending ? 'clickable' : ''}`}
              style={{ 
                display: 'inline-flex',
                alignItems: 'center',
                flexShrink: 0,
                minWidth: '70px',
                margin: '0 10px',
                lineHeight: '48px',
                color: isPending ? '#faad14' : 
                      (matter.approval === 2 && matter.outsendStatus === 2) ? '#52c41a' : 
                      (matter.approval === 3 || matter.outsendStatus === 3) ? '#ff4d4f' : '#999999',
                cursor: isPending ? 'pointer' : 'default',
                visibility: 'visible',
                opacity: 1, 
                zIndex: 1,
                textDecoration: isPending ? 'underline' : 'none',
                textUnderlineOffset: isPending ? '2px' : '0',
              }}
              onClick={(e) => {
                if (isPending) {
                  e.stopPropagation();
                  e.preventDefault();
                  this.showApprovalModal(matter);
                }
              }}
            >
              {approvalIcon}
              <span style={{ marginLeft: approvalIcon ? '4px' : '0' }}>
                {approvalText}
              </span>
            </span>
          </Tooltip>
        )}
      </div>
    );
  }

  getHandles() {
    const { matter } = this.props;
    const isPdf = this.isPdfFile(matter);
    const hasDownloadPermission = this.checkDownloadPermission();
    const isPending = matter.approval === 1 || matter.outsendStatus === 1;

    const handles = [
      <div
        className="cell-btn navy"
        onClick={(e) =>
          SafeUtil.stopPropagationWrap(e)(this.props.onGoDetail?.(matter))
        }
      >
        <InfoCircleOutlined className="btn-action mr5" />
        {Lang.t('matter.fileDetail')}
      </div>,
      
      // PDF转换选项 - 只在有下载权限且是PDF文件时显示
      ...(isPdf && hasDownloadPermission
        ? [
            <div
              className="cell-btn navy"
              onClick={(e) =>
                SafeUtil.stopPropagationWrap(e)(this.openPdfConvertModal())
              }
            >
              <FileImageOutlined className="btn-action mr5" />
              {Lang.t('matter.pdfToImage')}
            </div>,
          ]
        : []),
      
      ...(this.checkRenamePermission()
        ? [
            <div
              className="cell-btn navy"
              onClick={(e) =>
                SafeUtil.stopPropagationWrap(e)(this.prepareRename())
              }
            >
              <EditOutlined className="btn-action mr5" />
              {Lang.t('matter.rename')}
            </div>,
          ]
        : []),
      ...(hasDownloadPermission
        ? [
            <div
              className="cell-btn navy"
              onClick={(e) => SafeUtil.stopPropagationWrap(e)(matter.download())}
            >
              <DownloadOutlined className="btn-action mr5" />
              {Lang.t('matter.download')}
            </div>,
          ]
        : []),
      
      // 移动端审批操作 - 只在移动端、管理员、待审批状态显示
      ...(this.props.isMobile && this.checkAdminPermission() && isPending
        ? [
            <div
              className="cell-btn navy"
              onClick={(e) => {
                SafeUtil.stopPropagationWrap(e)();
                this.handleApprove(matter);
              }}
            >
              <CheckCircleOutlined className="btn-action mr5" />
              {Lang.t('matter.pass')}
            </div>,
            <div
              className="cell-btn text-danger"
              onClick={(e) => {
                SafeUtil.stopPropagationWrap(e)();
                this.handleReject(matter);
              }}
            >
              <CloseCircleOutlined className="btn-action mr5" />
              {Lang.t('matter.reject')}
            </div>,
          ]
        : []),
      
      ...(this.checkDeletePermission()
        ? [
            <div
              className="cell-btn text-danger"
              onClick={(e) =>
                SafeUtil.stopPropagationWrap(e)(this.deleteMatter())
              }
            >
              <DeleteOutlined className="btn-action mr5" />
              {Lang.t('matter.delete')}
            </div>,
          ]
        : []),
    ];

    return handles;
  }

  renderMobileOperation() {
    const { matter } = this.props;
    const approvalText = this.getApprovalText(matter.approval, matter.outsendStatus);
    const approvalIcon = this.getApprovalIcon(matter.approval, matter.outsendStatus);
    const approvalClassName = this.getApprovalClassName(matter.approval);

    return (
      <div className="more-panel">
        <div className="cell-btn navy text">
          <span>{DateUtil.simpleDateHourMinute(matter.updateTime)}</span>
          <span className="matter-size">
            {StringUtil.humanFileSize(matter.size)}
          </span>
          <span className={`matter-approval ${approvalClassName}`}>
            {approvalIcon}
            {approvalText}
          </span>
        </div>
        {this.getHandles()}
      </div>
    );
  }

  // 简化PDF转换模态框，移除存储路径设置
  renderPdfConvertModal() {
    const { pdfConvertModalVisible, pdfConvertFormat, pdfConverting } = this.state;
    const { matter } = this.props;

    return (
      <Modal
        title={Lang.t('matter.pdfToImage')}
        visible={pdfConvertModalVisible}
        onCancel={this.closePdfConvertModal}
        onOk={this.handlePdfConvert}
        confirmLoading={pdfConverting}
        okText={Lang.t('confirm')}
        cancelText={Lang.t('cancel')}
        width={400}
      >
        <div className="pdf-convert-modal">
          <div className="form-item">
            <label className="form-label">{Lang.t('matter.sourceFile')}:</label>
            <div className="form-value">{matter.name}</div>
          </div>
          
          <div className="form-item">
            <label className="form-label">{Lang.t('matter.imageFormat')}:</label>
            <Select
              value={pdfConvertFormat}
              onChange={(value) => this.setState({ pdfConvertFormat: value })}
              style={{ width: '100%' }}
            >
              <Option value="PNG">PNG ({Lang.t('matter.losslessCompression')})</Option>
              <Option value="JPEG">JPEG ({Lang.t('matter.lossyCompression')})</Option>
              <Option value="TIFF">TIFF ({Lang.t('matter.highQuality')})</Option>
              <Option value="SVG">SVG ({Lang.t('matter.vectorImage')})</Option>
            </Select>
          </div>
          
          <div className="form-tip" style={{ marginTop: '16px', color: '#666', fontSize: '12px' }}>
            {Lang.t('matter.pdfConvertTip')}
          </div>
        </div>
      </Modal>
    );
  }

  render() {
    const { matter } = this.props;

    const menu = (
      <Menu>
        {this.getHandles().map((item, i) => (
          <Menu.Item key={i}>{item}</Menu.Item>
        ))}
      </Menu>
    );

    return (
      <Dropdown overlay={menu} trigger={['contextMenu']}>
        <div className="widget-matter-panel">
          <div
            onClick={(e) => SafeUtil.stopPropagationWrap(e)(this.clickRow())}
          >
            <div className="media clearfix">
              <div className="pull-left">
                <div className="left-part">
                  <span
                    className="cell cell-hot"
                    onClick={(e) => SafeUtil.stopPropagationWrap(e)}
                  >
                    <Checkbox
                      checked={matter.check}
                      onChange={(e) => this.checkToggle(e)}
                    />
                  </span>
                  <span className="cell">
                    <img className="matter-icon" src={matter.getIcon()} />
                  </span>
                </div>
              </div>

              <div className="pull-right visible-pc">
                {matter.uuid && this.renderPcOperation()}
              </div>

              <div className="pull-right visible-mobile">
                <span
                  className="more-btn"
                  onClick={(e) =>
                    SafeUtil.stopPropagationWrap(e)(this.toggleHandles())
                  }
                >
                  <EllipsisOutlined className="btn-action navy f18" />
                </span>
              </div>

              <div className="media-body">
                <div className="middle-part">
                  {matter.editMode ? (
                    <span className="matter-name-edit">
                      <input
                        ref={this.inputRef}
                        className={matter.uuid!}
                        value={this.renameMatterName}
                        onChange={(e) => this.changeMatterName(e)}
                        placeholder={Lang.t('matter.enterName')}
                        onBlur={() => this.blurTrigger()}
                        onKeyUp={(e) => this.enterTrigger(e)}
                      />
                    </span>
                  ) : (
                    <span className="matter-name">
                      {matter.name}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Expanding>
            {this.showMore ? this.renderMobileOperation() : null}
          </Expanding>
          
          <ApprovalModal
            matter={this.state.currentMatter}
            visible={this.state.approvalModalVisible}
            onClose={this.handleApprovalModalClose}
            onApprovalSuccess={this.handleApprovalSuccess}
          />
          
          {this.renderPdfConvertModal()}
        </div>
      </Dropdown>
    );
  }
}